import { useRef, useEffect, useState } from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import { ChevronDown } from 'lucide-react';
import { EventImage } from '@/components/common/EventImage';

export interface EventSelectorEvent {
  id: string;
  name: string;
  is_active: boolean;
  image?: string | null;
}

interface EventSelectorProps {
  events: EventSelectorEvent[];
  selectedEventId: string | null;
  onSelect: (eventId: string) => void;
  loading?: boolean;
  noMargin?: boolean;
}

export function EventSelector({ events, selectedEventId, onSelect, loading, noMargin }: EventSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const selectedEvent = events.find((e) => e.id === selectedEventId);

  return (
    <Box
      ref={ref}
      position="relative"
      bg="volyx.bg"
      borderRadius="2xl"
      boxShadow="0 8px 20px rgba(0,0,0,0.12)"
      px="4"
      h="56px"
      mb={noMargin ? 0 : '3'}
      transition="all 0.3s"
      display="flex"
      alignItems="center"
      _hover={{ boxShadow: '0 12px 30px rgba(0,0,0,0.16)' }}
    >
      <Flex alignItems="center" gap="3" cursor="pointer" onClick={() => setOpen((o) => !o)}>
        <Box
          w="40px"
          h="40px"
          borderRadius="full"
          bg="volyx.primaryLight"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          overflow="hidden"
        >
          {selectedEvent?.image ? (
            <EventImage
              key={selectedEventId ?? 'none'}
              stored={selectedEvent.image}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Text fontSize="sm" fontWeight="bold" color="volyx.primary">
              {(selectedEvent?.name || 'E')[0].toUpperCase()}
            </Text>
          )}
        </Box>
        <Box flex="1">
          <Text fontSize="sm" fontWeight="600" color="volyx.primary" m="0">
            {loading ? '...' : selectedEvent?.name ?? 'Select event'}
          </Text>
        </Box>
        <ChevronDown
          size={18}
          style={{ color: 'var(--chakra-colors-volyx-textSecondary)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
        />
      </Flex>

      {open && (
        <Box
          position="absolute"
          top="100%"
          left="0"
          right="0"
          mt="1"
          bg="volyx.bg"
          borderRadius="xl"
          boxShadow="0 8px 24px rgba(0,0,0,0.12)"
          border="1px solid"
          borderColor="volyx.borderLight"
          zIndex={50}
          maxH="280px"
          overflowY="auto"
        >
          {events.map((event) => {
            const isSelected = event.id === selectedEventId;
            return (
              <Box
                key={event.id}
                px="4"
                py="3"
                cursor="pointer"
                bg={isSelected ? 'volyx.lightGray' : 'transparent'}
                _hover={{ bg: isSelected ? 'volyx.lightGray' : '#F9FAFB' }}
                onClick={() => { onSelect(event.id); setOpen(false); }}
                borderBottom="1px solid"
                borderColor="volyx.lightGray"
                _last={{ borderBottom: 'none' }}
              >
                <Flex alignItems="center" gap="3">
                  <Box
                    w="32px"
                    h="32px"
                    borderRadius="full"
                    bg="volyx.primaryLight"
                    flexShrink={0}
                    overflow="hidden"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {event.image ? (
                      <EventImage stored={event.image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Text fontSize="xs" fontWeight="bold" color="volyx.primary">
                        {(event.name || 'E')[0].toUpperCase()}
                      </Text>
                    )}
                  </Box>
                  <Flex flex="1" alignItems="center" justifyContent="space-between" gap="2" minW="0">
                    <Text fontSize="sm" fontWeight="semibold" color="volyx.primary" truncate m="0">
                      {event.name}
                    </Text>
                    <Box
                      px="2"
                      py="0.5"
                      borderRadius="full"
                      fontSize="10px"
                      fontWeight="semibold"
                      bg={event.is_active ? 'volyx.success' : 'volyx.error'}
                      color="white"
                      flexShrink={0}
                    >
                      {event.is_active ? 'Active' : 'Ended'}
                    </Box>
                  </Flex>
                </Flex>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
